import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Spinner } from "@heroui/react";
import { api } from "../api/client";
import { SHOP_ZONE_ORDER, SHOP_ZONE_LABELS } from "../constants";
import { PhotoPicker } from "../components/PhotoPicker";
import type { IngredientDetail } from "../api/client";

interface FormData {
  name: string;
  nameEn?: string;
  imageUrl?: string | null;
  defaultUnit?: string;
  gramsPerUnit?: number;
  fdcId?: number | null;
  barcode?: string;
  nutriSource?: string;
  state?: string;
  calories?: number;
  fat?: number;
  saturatedFat?: number;
  polyunsaturatedFat?: number;
  monounsaturatedFat?: number;
  transFat?: number;
  cholesterol?: number;
  sodium?: number;
  potassium?: number;
  carbs?: number;
  fiber?: number;
  sugar?: number;
  protein?: number;
  vitaminA?: number;
  vitaminC?: number;
  vitaminD?: number;
  vitaminB12?: number;
  magnesium?: number;
  zinc?: number;
  calcium?: number;
  iron?: number;
  allergens?: string;
  novaGroup?: number;
  additiveRiskLevel?: string;
  shopZone?: string;
  isPantry?: boolean;
  canBeMeal?: boolean;
}

const NOVA_DESC: Record<number, string> = {
  1: "Sense processar o mínimament processat",
  2: "Ingredient culinari processat",
  3: "Aliment processat",
  4: "Ultraprocessat",
};

// Estat de l'aliment al qual corresponen els valors per 100 g; sense valor = cru
const STATE_LABELS: Record<string, string> = {
  raw: "cru",
  cooked: "cuit",
  fried: "fregit",
  dried: "sec/deshidratat",
  canned: "en conserva",
  frozen: "congelat",
};

export function IngredientEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<FormData>({ name: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(id ? true : false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.get<IngredientDetail>(`/ingredients/${id}`).then((ing) => {
      setData({
        name: ing.name,
        nameEn: ing.nameEn || undefined,
        imageUrl: ing.imageUrl || null,
        defaultUnit: ing.defaultUnit || undefined,
        gramsPerUnit: ing.gramsPerUnit || undefined,
        fdcId: ing.fdcId || undefined,
        barcode: ing.barcode || undefined,
        nutriSource: ing.nutriSource || undefined,
        state: ing.state || undefined,
        calories: ing.calories || undefined,
        fat: ing.fat || undefined,
        saturatedFat: ing.saturatedFat || undefined,
        polyunsaturatedFat: ing.polyunsaturatedFat || undefined,
        monounsaturatedFat: ing.monounsaturatedFat || undefined,
        transFat: ing.transFat || undefined,
        cholesterol: ing.cholesterol || undefined,
        sodium: ing.sodium || undefined,
        potassium: ing.potassium || undefined,
        carbs: ing.carbs || undefined,
        fiber: ing.fiber || undefined,
        sugar: ing.sugar || undefined,
        protein: ing.protein || undefined,
        vitaminA: ing.vitaminA || undefined,
        vitaminC: ing.vitaminC || undefined,
        vitaminD: ing.vitaminD || undefined,
        vitaminB12: ing.vitaminB12 || undefined,
        magnesium: ing.magnesium || undefined,
        zinc: ing.zinc || undefined,
        calcium: ing.calcium || undefined,
        iron: ing.iron || undefined,
        allergens: ing.allergens || undefined,
        novaGroup: ing.novaGroup || undefined,
        additiveRiskLevel: ing.additiveRiskLevel || undefined,
        shopZone: ing.shopZone || undefined,
        isPantry: ing.isPantry ?? false,
        canBeMeal: ing.canBeMeal ?? false,
      });
      setLoading(false);
    }).catch((err) => {
      setError(err?.message || "Error carregant l'ingredient");
      setLoading(false);
    });
  }, [id]);

  async function handleSearch() {
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    try {
      const results = await api.get<any[]>(`/ingredients/search-external?q=${encodeURIComponent(q)}`);
      setSearchResults(results);
      setShowSearchResults(true);
    } catch (e) {
      console.error("Error searching:", e);
    } finally {
      setSearching(false);
    }
  }

  function handleSelectProduct(result: any) {
    // Substitueix TOT el perfil nutricional amb el de la font externa: els camps
    // que la font no té es buiden (res de valors vells barrejats) i els 0 es conserven.
    const n = (v: number | null | undefined) => (typeof v === "number" ? v : undefined);
    setData((d) => ({
      ...d,
      nameEn: result.name,
      fdcId: result.source === "USDA" ? parseInt(result.externalId) : undefined,
      barcode: result.source === "OFF" ? result.externalId : undefined,
      nutriSource: result.source,
      imageUrl: result.imageUrl || d.imageUrl,
      calories: n(result.nutrition?.calories),
      protein: n(result.nutrition?.protein),
      carbs: n(result.nutrition?.carbs),
      fat: n(result.nutrition?.fat),
      fiber: n(result.nutrition?.fiber),
      sugar: n(result.nutrition?.sugar),
      sodium: n(result.nutrition?.sodium),
      potassium: n(result.nutrition?.potassium),
      saturatedFat: n(result.nutrition?.saturatedFat),
      polyunsaturatedFat: n(result.nutrition?.polyunsaturatedFat),
      monounsaturatedFat: n(result.nutrition?.monounsaturatedFat),
      transFat: n(result.nutrition?.transFat),
      cholesterol: n(result.nutrition?.cholesterol),
      vitaminA: n(result.nutrition?.vitaminA),
      vitaminC: n(result.nutrition?.vitaminC),
      vitaminD: n(result.nutrition?.vitaminD),
      vitaminB12: n(result.nutrition?.vitaminB12),
      magnesium: n(result.nutrition?.magnesium),
      zinc: n(result.nutrition?.zinc),
      calcium: n(result.nutrition?.calcium),
      iron: n(result.nutrition?.iron),
      novaGroup: result.novaGroup ?? d.novaGroup,
      additiveRiskLevel: result.additiveRiskLevel ?? d.additiveRiskLevel,
      allergens: result.allergens ?? d.allergens,
    }));
    setShowSearchResults(false);
    setSearchQuery("");
  }

  async function handleSync(source?: "USDA" | "OFF") {
    if (!id) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const qs = source ? `?source=${source}` : "";
      const result = await api.get<any>(`/ingredients/${id}/sync${qs}`);
      handleSelectProduct(result);
      const src = result.source === "USDA" ? "USDA" : "Open Food Facts";
      if (result.stateMismatch) {
        const declared = STATE_LABELS[data.state ?? "raw"] ?? "cru";
        setSyncResult({
          ok: false,
          msg: `Dades actualitzades des de ${src}, però el producte de la font ("${result.name}") sembla ${STATE_LABELS[result.stateMismatch]} i l'ingredient està marcat com a ${declared}. Revisa l'enllaç abans de desar.`,
        });
      } else {
        setSyncResult({ ok: true, msg: `Dades actualitzades des de ${src}. Revisa i desa.` });
      }
    } catch (e: any) {
      setSyncResult({ ok: false, msg: e?.message || "Error en la sincronització" });
    } finally {
      setSyncing(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: data.name,
        nameEn: data.nameEn || null,
        imageUrl: data.imageUrl || null,
        defaultUnit: data.defaultUnit || null,
        gramsPerUnit: data.gramsPerUnit || null,
        fdcId: data.fdcId || null,
        barcode: data.barcode || null,
        nutriSource: data.nutriSource || null,
        state: data.state || null,
        calories: data.calories || null,
        fat: data.fat || null,
        saturatedFat: data.saturatedFat || null,
        polyunsaturatedFat: data.polyunsaturatedFat || null,
        monounsaturatedFat: data.monounsaturatedFat || null,
        transFat: data.transFat || null,
        cholesterol: data.cholesterol || null,
        sodium: data.sodium || null,
        potassium: data.potassium || null,
        carbs: data.carbs || null,
        fiber: data.fiber || null,
        sugar: data.sugar || null,
        protein: data.protein || null,
        vitaminA: data.vitaminA || null,
        vitaminC: data.vitaminC || null,
        vitaminD: data.vitaminD || null,
        vitaminB12: data.vitaminB12 || null,
        magnesium: data.magnesium || null,
        zinc: data.zinc || null,
        calcium: data.calcium || null,
        iron: data.iron || null,
        allergens: data.allergens || null,
        novaGroup: data.novaGroup || null,
        additiveRiskLevel: data.additiveRiskLevel || null,
        shopZone: data.shopZone || null,
        isPantry: data.isPantry ?? false,
        canBeMeal: data.canBeMeal ?? false,
      };
      const result = id ? await api.patch(`/ingredients/${id}`, payload) : await api.post(`/ingredients`, payload);
      if ((result as any)?.error) {
        setError((result as any).error);
        return;
      }
      nav("/ingredients");
    } catch (err: any) {
      setError(err?.message || "Error al guardar l'ingredient");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    try {
      await api.del(`/ingredients/${id}`);
      window.dispatchEvent(new Event("cookmenu:data-changed"));
      nav("/ingredients");
    } catch (err: any) {
      if (err?.code === "INGREDIENT_IN_USE" && err?.recipes?.length > 0) {
        const recipeNames = err.recipes.map((r: any) => r.name).join(", ");
        const msg = `Aquest ingredient s'utilitza en: ${recipeNames}\n\nVols esborrar-lo definitivament? Es traurà de totes les receptes.`;
        if (confirm(msg)) {
          try {
            await api.del(`/ingredients/${id}?force=true`);
            window.dispatchEvent(new Event("cookmenu:data-changed"));
            nav("/ingredients");
          } catch {
            alert("Error esborrant l'ingredient");
          }
        }
      } else {
        alert("Error esborrant l'ingredient");
      }
    }
  }

  const set = (k: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setData((d) => ({ ...d, [k]: e.target.value }));
  const setNum = (k: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setData((d) => ({ ...d, [k]: e.target.value ? parseFloat(e.target.value) : undefined }));

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      {/* Barra enganxosa */}
      <div className="save-bar">
        <h1 style={{ fontSize: 24 }}>{id ? "Edita ingredient" : "Nou ingredient"}</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => nav("/ingredients")}
            className="rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-surface-secondary"
          >
            Cancel·la
          </button>
          <button
            type="submit"
            form="ingredientForm"
            disabled={busy}
            className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Desant…" : "Desa"}
          </button>
        </div>
      </div>

      {error && (
        <Alert color="danger" title="Error" className="mb-4">
          {error}
        </Alert>
      )}

      <form id="ingredientForm" onSubmit={submit} className="flex flex-col gap-4">
        {/* Nom + autofill */}
        <section className="card" style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="form-row2">
            <div className="field">
              <label htmlFor="name">Nom (català) *</label>
              <input id="name" required className="nm-input" value={data.name} onChange={set("name")} placeholder="Ex: Pebrot vermell" />
            </div>
            <div className="field">
              <label htmlFor="nameEn">Nom (anglès)</label>
              <input id="nameEn" className="nm-input" value={data.nameEn ?? ""} onChange={set("nameEn")} placeholder="Red pepper" />
            </div>
          </div>

          {/* Autofill USDA / OFF */}
          <div className="autofill">
            <span className="af-ic">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
            </span>
            <input
              className="nm-input"
              style={{ border: 0, background: "transparent", boxShadow: "none", flex: 1 }}
              placeholder="Cerca a USDA / Open Food Facts per omplir automàticament…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleSearch())}
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={searching}
              className="rounded-lg bg-accent-soft px-3 py-1.5 text-sm font-semibold text-accent-soft-foreground transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              {searching ? "…" : "Cerca"}
            </button>
          </div>

          {showSearchResults && searchResults.length > 0 && (
            <div className="flex flex-col gap-1.5 rounded-xl border border-border bg-surface-secondary p-3">
              <p className="section-title mb-1">Selecciona el producte correcte</p>
              <div className="max-h-52 overflow-y-auto flex flex-col gap-1">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectProduct(result)}
                    className="flex flex-col gap-0.5 rounded-lg border border-border bg-surface px-3 py-2 text-left transition-colors hover:bg-accent-soft"
                  >
                    <span className="text-sm font-bold">{result.displayName}</span>
                    <span className="text-xs text-muted">
                      {result.source === "USDA" ? `FDC: ${result.externalId}` : `OFF: ${result.externalId}`}
                      {result.nutrition?.calories ? ` · ${result.nutrition.calories.toFixed(0)} kcal` : ""}
                      {result.nutrition?.protein ? ` · ${result.nutrition.protein.toFixed(1)}g prot` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Unitat i grams per unitat */}
        <Collapsible title="Unitat i conversió" defaultOpen>
          <div className="nutri-edit">
            <div className="field">
              <label htmlFor="defaultUnit">Unitat per defecte</label>
              <input id="defaultUnit" className="nm-input" value={data.defaultUnit ?? ""} onChange={set("defaultUnit")} placeholder="g, ml, u, tbsp…" />
            </div>
            <div className="field">
              <label htmlFor="gramsPerUnit">Grams per unitat (u)</label>
              <input id="gramsPerUnit" className="nm-input" type="number" step="any" min="0" value={data.gramsPerUnit ?? ""} onChange={setNum("gramsPerUnit")} placeholder="Ex: 60 (ou), 150 (tomàquet)" />
            </div>
            <div className="field">
              <label htmlFor="state">Estat (nutricionalment)</label>
              <select
                id="state"
                className="nm-input"
                value={data.state ?? ""}
                onChange={set("state")}
              >
                <option value="">Sense especificar (cru)</option>
                <option value="raw">Cru</option>
                <option value="cooked">Cuit</option>
                <option value="fried">Fregit</option>
                <option value="dried">Sec / deshidratat</option>
                <option value="canned">En conserva</option>
                <option value="frozen">Congelat</option>
              </select>
            </div>
          </div>
        </Collapsible>

        {/* Macronutrients */}
        <Collapsible title="Macronutrients" sub="per 100 g" defaultOpen>
          <div className="nutri-edit">
            {([
              ["calories", "Calories", "kcal"],
              ["protein", "Proteïna", "g"],
              ["carbs", "Carbohidrats", "g"],
              ["fat", "Greix total", "g"],
              ["fiber", "Fibra", "g"],
              ["sugar", "Sucre", "g"],
            ] as [keyof FormData, string, string][]).map(([k, l, u]) => (
              <div key={k} className="field">
                <label htmlFor={k}>{l} ({u})</label>
                <input id={k} className="nm-input" type="number" step="any" value={(data[k] as number | undefined) ?? ""} onChange={setNum(k)} />
              </div>
            ))}
          </div>
        </Collapsible>

        {/* Tipus de greixos */}
        <Collapsible title="Tipus de greixos" sub="per 100 g">
          <div className="nutri-edit">
            {([
              ["saturatedFat", "Greix saturat", "g"],
              ["polyunsaturatedFat", "Poliinsaturat", "g"],
              ["monounsaturatedFat", "Monoinsaturat", "g"],
              ["transFat", "Greix trans", "g"],
              ["cholesterol", "Colesterol", "mg"],
            ] as [keyof FormData, string, string][]).map(([k, l, u]) => (
              <div key={k} className="field">
                <label htmlFor={k}>{l} ({u})</label>
                <input id={k} className="nm-input" type="number" step="any" value={(data[k] as number | undefined) ?? ""} onChange={setNum(k)} />
              </div>
            ))}
          </div>
        </Collapsible>

        {/* Minerals */}
        <Collapsible title="Minerals" sub="per 100 g">
          <div className="nutri-edit">
            {([
              ["calcium", "Calci", "mg"],
              ["iron", "Ferro", "mg"],
              ["magnesium", "Magnesi", "mg"],
              ["zinc", "Zinc", "mg"],
              ["sodium", "Sodi", "mg"],
              ["potassium", "Potassi", "mg"],
            ] as [keyof FormData, string, string][]).map(([k, l, u]) => (
              <div key={k} className="field">
                <label htmlFor={k}>{l} ({u})</label>
                <input id={k} className="nm-input" type="number" step="any" value={(data[k] as number | undefined) ?? ""} onChange={setNum(k)} />
              </div>
            ))}
          </div>
        </Collapsible>

        {/* Vitamines */}
        <Collapsible title="Vitamines" sub="per 100 g">
          <div className="nutri-edit">
            {([
              ["vitaminA", "Vitamina A", "μg"],
              ["vitaminC", "Vitamina C", "mg"],
              ["vitaminD", "Vitamina D", "μg"],
              ["vitaminB12", "Vitamina B12", "μg"],
            ] as [keyof FormData, string, string][]).map(([k, l, u]) => (
              <div key={k} className="field">
                <label htmlFor={k}>{l} ({u})</label>
                <input id={k} className="nm-input" type="number" step="any" value={(data[k] as number | undefined) ?? ""} onChange={setNum(k)} />
              </div>
            ))}
          </div>
        </Collapsible>

        {/* Compra */}
        <Collapsible title="Compra" defaultOpen>
          <div className="flex flex-col gap-4">
            <div className="form-row2">
              <div className="field">
                <label htmlFor="shopZone">Categoria</label>
                <select
                  id="shopZone"
                  className="nm-input"
                  value={data.shopZone ?? ""}
                  onChange={set("shopZone")}
                >
                  <option value="">Sense assignar</option>
                  {SHOP_ZONE_ORDER.map((zone) => (
                    <option key={zone} value={zone}>
                      {SHOP_ZONE_LABELS[zone]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>De rebost</label>
                <label className="flex items-center gap-2 cursor-pointer pt-1.5">
                  <input
                    type="checkbox"
                    checked={data.isPantry ?? false}
                    onChange={(e) => setData((d) => ({ ...d, isPantry: e.target.checked }))}
                  />
                  <span className="text-sm">No afegir a la llista de la compra</span>
                </label>
                <span className="text-xs text-muted">Espècies, sal, oli, mel…</span>
              </div>
              <div className="field">
                <label>Es pot menjar sol</label>
                <label className="flex items-center gap-2 cursor-pointer pt-1.5">
                  <input
                    type="checkbox"
                    checked={data.canBeMeal ?? false}
                    onChange={(e) => setData((d) => ({ ...d, canBeMeal: e.target.checked }))}
                  />
                  <span className="text-sm">Afegible sol al menú setmanal</span>
                </label>
                <span className="text-xs text-muted">Fruita, snacks, embotits, iogurt…</span>
              </div>
            </div>
          </div>
        </Collapsible>

        {/* Classificació */}
        <Collapsible title="Classificació i al·lèrgens" defaultOpen>
          <div className="flex flex-col gap-4">
            <div className="form-row2">
              <div className="field">
                <label htmlFor="novaGroup">Grup NOVA</label>
                <select
                  id="novaGroup"
                  className="nm-input"
                  value={data.novaGroup ?? ""}
                  onChange={(e) => setData((d) => ({ ...d, novaGroup: e.target.value ? parseInt(e.target.value) : undefined }))}
                >
                  <option value="">En blanc</option>
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>{n} — {NOVA_DESC[n]}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="allergens">Al·lèrgens</label>
                <input id="allergens" className="nm-input" value={data.allergens ?? ""} onChange={set("allergens")} placeholder="gluten, lactosa, ous…" />
              </div>
            </div>
            <div className="form-row2">
              <div className="field">
                <label htmlFor="additiveRiskLevel">Risc d'additius</label>
                <select
                  id="additiveRiskLevel"
                  className="nm-input"
                  value={data.additiveRiskLevel ?? ""}
                  onChange={set("additiveRiskLevel")}
                >
                  <option value="">Sense especificar</option>
                  <option value="zero">Zero (sense additius)</option>
                  <option value="low">Baix</option>
                  <option value="medium">Moderat</option>
                  <option value="high">Alt risc</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="nutriSource">Font de nutrició</label>
                <select
                  id="nutriSource"
                  className="nm-input"
                  value={data.nutriSource ?? ""}
                  onChange={set("nutriSource")}
                >
                  <option value="">Sense especificar</option>
                  <option value="USDA">USDA</option>
                  <option value="OFF">Open Food Facts</option>
                  <option value="BEDCA">BEDCA</option>
                  <option value="IA">IA</option>
                  <option value="MANUAL">Manual</option>
                </select>
              </div>
            </div>
            <div className="form-row2">
              <div className="field">
                <label htmlFor="fdcId">USDA FDC ID</label>
                <input id="fdcId" className="nm-input" type="number" value={data.fdcId ?? ""} onChange={(e) => setData((d) => ({ ...d, fdcId: e.target.value ? parseInt(e.target.value) : undefined }))} placeholder="Identificador USDA" />
              </div>
              <div className="field">
                <label htmlFor="barcode">Codi de barres</label>
                <input id="barcode" className="nm-input" value={data.barcode ?? ""} onChange={set("barcode")} placeholder="EAN / UPC (Open Food Facts)" />
              </div>
            </div>

            {/* Sincronització a demanda */}
            {id && (data.fdcId || data.barcode) && (
              <div className="flex flex-col gap-2 rounded-xl border border-border bg-surface-secondary px-4 py-3">
                <p className="text-sm font-semibold" style={{ fontFamily: "var(--font-head)" }}>Sincronitza dades nutricionals</p>
                <div className="flex flex-wrap gap-2">
                  {data.fdcId && (
                    <button
                      type="button"
                      disabled={syncing}
                      onClick={() => handleSync("USDA")}
                      className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-accent-soft disabled:opacity-50"
                    >
                      {syncing ? <Spinner size="sm" /> : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                      )}
                      USDA #{data.fdcId}
                    </button>
                  )}
                  {data.barcode && (
                    <button
                      type="button"
                      disabled={syncing}
                      onClick={() => handleSync("OFF")}
                      className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-accent-soft disabled:opacity-50"
                    >
                      {syncing ? <Spinner size="sm" /> : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                      )}
                      Open Food Facts
                    </button>
                  )}
                </div>
                {syncResult && (
                  <p className={`text-xs ${syncResult.ok ? "text-success" : "text-danger"}`}>
                    {syncResult.ok ? "✓" : "⚠"} {syncResult.msg}
                  </p>
                )}
              </div>
            )}
          </div>
        </Collapsible>

        {/* Imatge */}
        <Collapsible title="Imatge">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Imatge</label>
            <PhotoPicker
              type="ingredient"
              imageUrl={data.imageUrl}
              name={data.name}
              size={120}
              onChange={(url) => setData((d) => ({ ...d, imageUrl: url }))}
            />
          </div>
        </Collapsible>

        {/* Esborra */}
        {id && (
          <div className="mt-4 border-t border-border pt-4">
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-xl bg-danger-soft px-4 py-2 text-sm font-semibold text-danger transition-opacity hover:opacity-80"
            >
              Esborra l'ingredient
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function Collapsible({
  title,
  sub,
  defaultOpen = false,
  children,
}: {
  title: string;
  sub?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card" style={{ overflow: "hidden" }}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="collapsible-toggle">
        <span className="flex items-baseline gap-2.5">
          <span style={{ fontFamily: "var(--font-head)", fontWeight: 600, fontSize: 15 }}>{title}</span>
          {sub && <span className="text-muted" style={{ fontWeight: 700, fontSize: 12.5 }}>{sub}</span>}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "var(--faint)", transform: open ? "rotate(90deg)" : "none", transition: ".15s", flexShrink: 0 }}
          aria-hidden
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
      {open && <div className="collapsible-body">{children}</div>}
    </section>
  );
}
